import { useRef } from 'react';
import { Text } from '@mantine/core';
import { gql, useMutation } from '@apollo/client';
import { showNotification } from '@mantine/notifications';
import { IconFileSettings } from '@tabler/icons-react';
import useAccount from '@features/Account/store/useAccount';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import invokePromptConfirm from '@features/Prompt/PromptConfirm';
import type { ActionSignature } from '@helpers/file';
import type { ResourceScope } from '@app-types/blackroom-app';
import useCanManageResources from '../../hooks/useCanManageResources';
import { getViewAsUserId } from '@shared/providers/ViewAsProvider/store';

// Temporary production repair; keep its mutation and copy local for easy removal.
const REPAIR_TEXT_RESOURCES = gql`
  mutation RepairTextResources($input: RepairTextResourcesInput!) {
    repairTextResources(input: $input)
  }
`;
const label = 'Réparer les .txt';

export function useRepairTextResourcesAction(scope: ResourceScope = 'my') {
  const { account } = useAccount();
  const { id: workspaceId } = useWorkspace();
  const [repair] = useMutation<
    { repairTextResources: number },
    { input: { workspaceId: string; resourceIds: string[] } }
  >(REPAIR_TEXT_RESOURCES);
  const pending = useRef(false);
  const { canManageResourcesHere } = useCanManageResources();
  const visible =
    account?.email === 'kyllian.vinkler@gmail.com' &&
    !getViewAsUserId() &&
    canManageResourcesHere &&
    scope !== 'archived';

  return {
    label,
    iconComponent: IconFileSettings,
    visible,
    action: (({ resourceIds = [], resetRowSelection }) => {
      if (!visible || getViewAsUserId()) return;
      if (pending.current || resourceIds.length === 0) return;
      if (resourceIds.length > 500) {
        showNotification({
          title: label,
          message: 'Sélectionne au maximum 500 fichiers.',
          color: 'blackroomOrange',
        });
        return;
      }
      const selectedIds = [...new Set(resourceIds)];
      invokePromptConfirm({
        componentProps: {
          modalContent: (
            <Text>
              Relancer le traitement des .txt sélectionnés ? Les variantes
              existantes seront conservées. Les dossiers et fichiers caviardés
              seront ignorés.
            </Text>
          ),
        },
        onConfirm: async () => {
          if (pending.current) return;
          pending.current = true;
          try {
            const { data } = await repair({
              variables: { input: { workspaceId, resourceIds: selectedIds } },
            });
            if (!data) throw new Error('Missing repair response');
            const count = data.repairTextResources;
            showNotification({
              title: label,
              message: count
                ? `Traitement demandé pour ${count} fichier(s) .txt en arrière-plan.`
                : 'Aucun fichier .txt dans la sélection.',
              color: count ? 'blackroomGreen' : 'blackroomOrange',
            });
            resetRowSelection?.();
          } catch (error) {
            showNotification({
              title: label,
              message:
                'Impossible de lancer la réparation. Réessaie ou consulte les logs.',
              color: 'blackroomRed',
            });
            throw error;
          } finally {
            pending.current = false;
          }
        },
      });
    }) satisfies ActionSignature,
  };
}
